import { useMemo, useState, useEffect, useRef } from 'react';

export const usePaginatedFilteredSortedData = (groupedResults, allData, initialItemsPerPage = 10) => {
  const [filterText, setFilterText] = useState('');
  const [debouncedFilterText, setDebouncedFilterText] = useState(filterText);
  const debounceTimer = useRef(null);

  const [sortOption, setSortOption] = useState('euNumber');
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => setDebouncedFilterText(filterText), 300);
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [filterText]);

  const filteredAndSortedEuGroups = useMemo(() => {
    const groupsArray = Object.entries(groupedResults).map(([euNumber, documentsMap]) => {
      const formattedEuNumber = euNumber.replace(/-/g, '/');
      const medicineItem = allData?.find(item => item.eu_pnumber.replace(/-/g,'/') === formattedEuNumber);
      const imageCount = Object.values(documentsMap).reduce((total, doc) => total + doc.images.length, 0);
      return {
        euNumber,
        documentsMap,
        documentCount: Object.keys(documentsMap).length,
        imageCount,
        medicineName: medicineItem?.eu_brand_name_current || '',
        activeSubstance: medicineItem?.active_substance_current || '',
        displayName: `${formattedEuNumber} - ${medicineItem?.eu_brand_name_current || 'N/A'} (${medicineItem?.active_substance_current || 'N/A'})`
      };
    });
    const preprocessed = groupsArray.map(g => ({ ...g, lowerEuNumber: g.euNumber.toLowerCase(), lowerMedicineName: g.medicineName.toLowerCase(), lowerActiveSubstance: g.activeSubstance.toLowerCase() }));
    const filtered = debouncedFilterText
      ? preprocessed.filter(g =>
          g.lowerEuNumber.includes(debouncedFilterText.toLowerCase()) ||
          g.lowerMedicineName.includes(debouncedFilterText.toLowerCase()) ||
          g.lowerActiveSubstance.includes(debouncedFilterText.toLowerCase())
        )
      : preprocessed;

    return [...filtered].sort((a, b) => {
      let comparison = 0;
      if (sortOption === 'euNumber') {
        const lastA = a.euNumber.substring(a.euNumber.lastIndexOf('-') + 1);
        const lastB = b.euNumber.substring(b.euNumber.lastIndexOf('-') + 1);
        comparison = lastA.localeCompare(lastB, undefined, { numeric: true });
      } else if (sortOption === 'name') {
        comparison = a.medicineName.localeCompare(b.medicineName);
      } else if (sortOption === 'activeSubstance') {
        comparison = a.activeSubstance.localeCompare(b.activeSubstance);
      } else if (sortOption === 'hits') {
        comparison = a.documentCount - b.documentCount;
      } else if (sortOption === 'imageCount') {
        comparison = a.imageCount - b.imageCount;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [groupedResults, debouncedFilterText, sortOption, sortDirection, allData]);

  useEffect(() => { setCurrentPage(1); }, [debouncedFilterText, sortOption, sortDirection, itemsPerPage]);

  const paginatedEuGroups = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedEuGroups.slice(start, start + itemsPerPage);
  }, [filteredAndSortedEuGroups, currentPage, itemsPerPage]);

  const totalPages = useMemo(() => Math.ceil(filteredAndSortedEuGroups.length / itemsPerPage), [filteredAndSortedEuGroups, itemsPerPage]);

  const handleSortChange = (option) => {
    if (sortOption === option) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortOption(option);
      setSortDirection(option === 'hits' || option === 'imageCount' ? 'desc' : 'asc'); // Default sort for 'hits' and 'imageCount'
    }
  };

  return {
    filterText,
    setFilterText,
    debouncedFilterText, // for display purposes if needed
    sortOption,
    sortDirection,
    handleSortChange,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    paginatedEuGroups,
    totalPages,
    filteredAndSortedEuGroups, // Full filtered list for counts/exports
  };
};